import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { CacheBustingInterceptor } from './interceptors/cache-busting.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';

import { SignInComponent } from './routes/sign-in/sign-in.component';
import { KalpaIconComponent } from './assets/icons/kalpa-icon/kalpa-icon.component';
import { HttpClientModule } from '@angular/common/http';
import { DashboardComponent } from './routes/dashboard/dashboard.component';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { LineIconComponent } from './assets/icons/line-icon/line-icon.component';
import { AuthService } from './services/auth.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialogModule } from '@angular/material/dialog';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { ManageCoursesIconComponent } from './assets/icons/manage-courses-icon/manage-courses-icon.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { SystemSettingsComponent } from './routes/system-settings/system-settings.component';
import { SurveysComponent } from './routes/surveys/surveys.component';
import { PostsComponent } from './routes/bulletin-board/posts.component';
import { ViewPostComponent } from './routes/bulletin-board/view-post/view-post.component';
import { EditPostComponent } from './routes/bulletin-board/edit-post/edit-post.component';
import { WaitlistsComponent } from './routes/manage-courses/components/waitlists/waitlists.component';
import { WaitlistComponent } from './routes/manage-courses/components/waitlists/waitlist/waitlist.component';
import { Amplify } from 'aws-amplify';
import { environment } from '../environments/environment';
import { PositionsComponent } from './routes/system-settings/components/positions/positions.component';
import { BuildingsComponent } from './routes/system-settings/components/buildings/buildings.component';
import { TableComponent } from './components/table/table.component';
import { DetailsDialogComponent } from './routes/system-settings/components/buildings/components/details-dialog/details-dialog.component';
import { PositionDetailsDialogComponent } from './routes/system-settings/components/positions/components/position-details-dialog/position-details-dialog.component';
import { UpdatePositionComponent } from './routes/system-settings/components/positions/components/update-position/update-position.component';
import { UpdateBuildingComponent } from './routes/system-settings/components/buildings/components/update-building/update-building.component';
import { VerificationComponent } from './components/verification/verification.component';
import { RequirementsComponent } from './routes/system-settings/components/requirements/requirements.component';
import { TagsComponent } from './routes/system-settings/components/tags/tags.component';
import { UpdateTagComponent } from './routes/system-settings/components/tags/components/update-tag/update-tag.component';
import { BatchReassignComponent } from './components/batch-reassign/batch-reassign.component';
import { DefaultsComponent } from './routes/system-settings/components/defaults/defaults.component';
import { GeneralComponent } from './routes/system-settings/components/defaults/components/general/general.component';
import { TooltipComponent } from './components/tooltip/tooltip.component';
import { ApprovalComponent } from './routes/system-settings/components/defaults/components/approval/approval.component';
import { RegistrationNoticeComponent } from './routes/system-settings/components/defaults/components/registration-notice/registration-notice.component';
import { RegistrationMessageComponent } from './routes/system-settings/components/defaults/components/registration-message/registration-message.component';
import { EmailNotificationsComponent } from './routes/system-settings/components/defaults/components/email-notifications/email-notifications.component';
import { TagDetailsComponent } from './routes/system-settings/components/tags/components/tag-details/tag-details.component';
import { ChipComponent } from './components/chip/chip.component';
import { CourseTypesComponent } from './routes/system-settings/components/course-types/course-types.component';
import { UpdateCourseTypeComponent } from './routes/system-settings/components/course-types/components/update-course-type/update-course-type.component';
import { CourseTypeDetailsComponent } from './routes/system-settings/components/course-types/components/course-type-details/course-type-details.component';
import { SwapComponent } from './components/swap/swap.component';
import { MatIconModule } from '@angular/material/icon';
import { UserRolesComponent } from './routes/system-settings/components/user-roles/user-roles.component';
import { UpdateRequirementComponent } from './routes/system-settings/components/requirements/components/update-requirement/update-requirement.component';
import { RequirementDetailsComponent } from './routes/system-settings/components/requirements/components/requirement-details/requirement-details.component';
import { ToastComponent } from './components/toast/toast.component';
import { InputComponent } from './components/input/input.component';
import { SelectComponent } from './components/select/select.component';
import { ManageStaffComponent } from './routes/manage-staff/manage-staff.component';
import { StaffListComponent } from './routes/manage-staff/components/staff-list/staff-list.component';
import { UpdateUserComponent } from './routes/manage-staff/components/staff-list/components/update-user/update-user.component';
import { CourseCatalogComponent } from './routes/manage-courses/components/course-catalog/course-catalog.component';
import { UpdateCourseComponent } from './routes/manage-courses/components/course-catalog/components/update-course/update-course.component';
import { ProfileComponent } from './routes/profile/profile.component';
import { MyPdPlanComponent } from './routes/profile/components/my-pd-plan/my-pd-plan.component';
import { MyCoursesComponent } from './routes/profile/components/my-pd-plan/components/my-courses/my-courses.component';
import { UpdateStatusModalComponent } from './routes/profile/components/my-pd-plan/components/my-courses/components/update-status-modal/update-status-modal.component';
import { ValidationComponent } from './routes/manage-courses/components/validation/validation.component';
import { CourseValidationDetailsComponent } from './routes/manage-courses/components/validation/components/course-validation-details/course-validation-details.component';
import { CourseRequestsComponent } from './routes/manage-courses/components/course-requests/course-requests.component';
import { CourseRequestResponseComponent } from './routes/manage-courses/components/course-requests/components/course-request-response/course-request-response.component';
import { MyCourseRequestsComponent } from './routes/profile/components/my-course-requests/my-course-requests.component';
import { UpdateMyCourseRequestComponent } from './routes/profile/components/my-course-requests/components/update-my-course-request/update-my-course-request.component';
import { MyProfileComponent } from './routes/profile/components/my-profile/my-profile.component';
import { ReassignStaffComponent } from './routes/manage-staff/components/reassign-staff/reassign-staff.component';
import { BuildingReassignmentComponent } from './routes/manage-staff/components/reassign-staff/components/building-reassignment/building-reassignment.component';
import { PrimaryManagerReassignmentComponent } from './routes/manage-staff/components/reassign-staff/components/primary-manager-reassignment/primary-manager-reassignment.component';
import { SecondaryManagerReassignmentComponent } from './routes/manage-staff/components/reassign-staff/components/secondary-manager-reassignment/secondary-manager-reassignment.component';
import { RequirementReassignmentComponent } from './routes/manage-staff/components/reassign-staff/components/requirement-reassignment/requirement-reassignment.component';
import { UserRolesReassignmentComponent } from './routes/manage-staff/components/reassign-staff/components/user-roles-reassignment/user-roles-reassignment.component';
import { GeneralInfoComponent } from './routes/manage-courses/components/course-catalog/components/update-course/components/general-info/general-info.component';
import { CourseDetailsComponent } from './routes/manage-courses/components/course-catalog/components/update-course/components/course-details/course-details.component';
import { CourseSortingComponent } from './routes/manage-courses/components/course-catalog/components/update-course/components/course-sorting/course-sorting.component';
import { AdditionalDetailsComponent } from './routes/manage-courses/components/course-catalog/components/update-course/components/additional-details/additional-details.component';
import { AttachmentsComponent } from './routes/manage-courses/components/course-catalog/components/update-course/components/attachments/attachments.component';
import { FinishComponent } from './routes/manage-courses/components/course-catalog/components/update-course/components/finish/finish.component';
import { StepperComponent } from './components/stepper/stepper.component';
import { UserGeneralInfoComponent } from './routes/manage-staff/components/staff-list/components/update-user/components/user-general-info/user-general-info.component';
import { BuildingsAndPositionsComponent } from './routes/manage-staff/components/staff-list/components/update-user/components/buildings-and-positions/buildings-and-positions.component';
import { UserAssignmentsComponent } from './routes/manage-staff/components/staff-list/components/update-user/components/user-assignments/user-assignments.component';
import { LicensesAndCertsComponent } from './routes/manage-staff/components/staff-list/components/update-user/components/licenses-and-certs/licenses-and-certs.component';
import { ReviewAndFinishComponent } from './routes/manage-staff/components/staff-list/components/update-user/components/review-and-finish/review-and-finish.component';
import { UserRequirementsComponent } from './routes/manage-staff/components/staff-list/components/update-user/components/user-requirements/user-requirements.component';
import { CourseFiltersComponent } from './routes/manage-courses/components/course-catalog/components/course-filters/course-filters.component';
import { GeneralCourseFiltersComponent } from './routes/manage-courses/components/course-catalog/components/course-filters/components/general-course-filters/general-course-filters.component';
import { CourseBuildingsFiltersComponent } from './routes/manage-courses/components/course-catalog/components/course-filters/components/course-buildings-filters/course-buildings-filters.component';
import { CourseRequirementsFiltersComponent } from './routes/manage-courses/components/course-catalog/components/course-filters/components/course-requirements-filters/course-requirements-filters.component';
import { CourseCategoriesFiltersComponent } from './routes/manage-courses/components/course-catalog/components/course-filters/components/course-categories-filters/course-categories-filters.component';
import { CourseTagsFiltersComponent } from './routes/manage-courses/components/course-catalog/components/course-filters/components/course-tags-filters/course-tags-filters.component';
import { CourseCourseTypesFiltersComponent } from './routes/manage-courses/components/course-catalog/components/course-filters/components/course-course-types-filters/course-course-types-filters.component';
import { CatalogCourseDetailsComponent } from './routes/manage-courses/components/course-catalog/components/catalog-course-details/catalog-course-details.component';
import { RegistrationRequirementModalComponent } from './routes/manage-courses/components/course-catalog/components/registration-requirement-modal/registration-requirement-modal.component';
import { RegistrationSuccessModalComponent } from './routes/manage-courses/components/course-catalog/components/registration-success-modal/registration-success-modal.component';
import { RegistrationConflictModalComponent } from './routes/manage-courses/components/course-catalog/components/registration-conflict-modal/registration-conflict-modal.component';
import { RegistrationWaitlistModalComponent } from './routes/manage-courses/components/course-catalog/components/registration-waitlist-modal/registration-waitlist-modal.component';
import { UpdateRequirementModalComponent } from './routes/profile/components/my-pd-plan/components/my-courses/components/update-requirement-modal/update-requirement-modal.component';
import { PushModalComponent } from './routes/manage-courses/components/course-catalog/components/catalog-course-details/components/push-modal/push-modal.component';
import { PushToGroupComponent } from './routes/manage-courses/components/course-catalog/components/catalog-course-details/components/push-modal/components/push-to-group/push-to-group.component';
import { QuickPeoplePushComponent } from './routes/manage-courses/components/course-catalog/components/catalog-course-details/components/push-modal/components/quick-people-push/quick-people-push.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { BulletinWidgetComponent } from './routes/dashboard/components/bulletin-widget/bulletin-widget.component';
import { CalendarWidgetComponent } from './routes/dashboard/components/calendar-widget/calendar-widget.component';
import { StatusWidgetComponent } from './routes/dashboard/components/status-widget/status-widget.component';
import { CatalogSuggestionsWidgetComponent } from './routes/dashboard/components/catalog-suggestions-widget/catalog-suggestions-widget.component';
import { CourseRequestsWidgetComponent } from './routes/dashboard/components/course-requests-widget/course-requests-widget.component';
import { ValidationWidgetComponent } from './routes/dashboard/components/validation-widget/validation-widget.component';
import { WaitlistWidgetComponent } from './routes/dashboard/components/waitlist-widget/waitlist-widget.component';
import { FullCalendarModule } from '@fullcalendar/angular';
import { KalpaRedComponent } from './assets/logos/kalpa-red/kalpa-red.component';
import { KalpaRedBlackComponent } from './assets/logos/kalpa-red-black/kalpa-red-black.component';
import { ManageCoursesComponent } from './routes/manage-courses/manage-courses.component';
import { BreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs.component';
import { CategoriesComponent } from './routes/system-settings/components/categories/categories.component';
import { UpdateCategoryComponent } from './routes/system-settings/components/categories/components/update-category/update-category.component';
import { CategoryDetailsComponent } from './routes/system-settings/components/categories/components/category-details/category-details.component';
import { CourseRequestGeneralInfoComponent } from './routes/profile/components/my-course-requests/components/update-my-course-request/components/course-request-general-info/course-request-general-info.component';
import { CourseRequestAttachmentsComponent } from './routes/profile/components/my-course-requests/components/update-my-course-request/components/course-request-attachments/course-request-attachments.component';
import { CourseRequestReviewComponent } from './routes/profile/components/my-course-requests/components/update-my-course-request/components/course-request-review/course-request-review.component';
import { TreeChecklistComponent } from './components/tree-checklist/tree-checklist.component';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { ForgotPasswordComponent } from './routes/sign-in/components/forgot-password/forgot-password.component';
import { LogInComponent } from './routes/sign-in/components/log-in/log-in.component';
import { FirstTimeLoginComponent } from './routes/sign-in/components/first-time-login/first-time-login.component';
import { PasswordChangeComponent } from './components/password-change/password-change.component';
import { SendCodeModalComponent } from './routes/sign-in/components/send-code-modal/send-code-modal.component';
import { ConfirmLoginComponent } from './routes/sign-in/components/confirm-login/confirm-login.component';
import { ChangePasswordModalComponent } from './routes/profile/components/change-password-modal/change-password-modal.component';
import { MyCourseRequestDetailsComponent } from './routes/profile/components/my-course-requests/components/my-course-request-details/my-course-request-details.component';
import { RequirementsListComponent } from './routes/profile/components/my-pd-plan/components/my-courses/components/requirements-list/requirements-list.component';
import { CoursesListComponent } from './routes/profile/components/my-pd-plan/components/my-courses/components/courses-list/courses-list.component';
import { CourseRequestDetailsComponent } from './routes/manage-courses/components/course-requests/components/course-request-details/course-request-details.component';
import { SharedComponentsModule } from './components/shared-components.module';
import { CompletePasswordChangeComponent } from './routes/sign-in/components/complete-password-change/complete-password-change.component';
import { AdvancedFiltersComponent } from './routes/manage-courses/components/course-requests/components/advanced-filters/advanced-filters.component';
import { ContentContainerComponent } from './components/_design-components/content-container/content-container.component';
import { StateDocumentExportComponent } from './routes/manage-courses/components/state-document-export/state-document-export.component';
import { ExportSettingsComponent } from './routes/manage-courses/components/state-document-export/export-settings/export-settings.component';
import { DocumentExportCoursesFilterComponent } from './routes/manage-courses/components/state-document-export/tabs/select-courses/courses-filter/courses-filter.component';
import { Error5401Component } from './routes/manage-courses/components/state-document-export/tabs/finish-export/error-5401/error-5401.component';
import { AddSurveyComponent } from './routes/surveys/add-survey/add-survey.component';
import { ButtonGroupComponent } from './components/_design-components/button-group/button-group.component';
import { NewStepperComponent } from './components/_design-components/stepper/stepper.component';
import { SearchFilterComponent } from './components/_design-components/search-filter/search-filter.component';
import { ModalComponent } from './components/_design-components/modal/modal.component';
import { SelectCoursesComponent } from './routes/manage-courses/components/state-document-export/tabs/select-courses/select-courses.component';
import { SelectRequirementsComponent } from './routes/manage-courses/components/state-document-export/tabs/select-requirements/select-requirements.component';
import { FinishAndExportComponent } from './routes/manage-courses/components/state-document-export/tabs/finish-export/finish-export.component';
import { SelectCoursesFilterComponent } from './routes/manage-courses/components/state-document-export/tabs/select-courses/modal/select-courses-filter/select-courses-filter.component';
import { NewInputComponent } from './components/_design-components/form-components/new-input/new-input.component';
import { FormSelectGroupComponent } from './components/_design-components/form-components/form-select-group/form-select-group.component';
import { FormSelectOptionComponent } from './components/_design-components/form-components/form-select-option/form-select-option.component';
import { FormDatePickerComponent } from './components/_design-components/form-components/form-date-picker/form-date-picker.component';
import { PopUpMenuComponent } from './components/_design-components/pop-up-menu/pop-up-menu.component';
import { SsoCallbackComponent } from './routes/sign-in/components/sso-callback/sso-callback.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UploadFileComponent } from './components/upload-file/upload-file.component';
import { EntityLogoComponent } from './components/entity-logo/entity-logo.component';
import { EditAvatarComponent } from './routes/profile/components/edit-avatar/edit-avatar.component';
import { EditorModule } from '@tinymce/tinymce-angular';
import { EditEntityLogoComponent } from './routes/system-settings/components/defaults/components/edit-entity-logo/edit-entity-logo.component';
import { RichTextEditorComponent } from './components/rich-text-editor/rich-text-editor.component';
import { RichTextEditorValidationDirective } from './components/rich-text-editor/rich-text-editor-validation.directive';
import { RichTextPresenterComponent } from './components/rich-text-presenter/rich-text-presenter.component';
import { ExpandingDrawerComponent } from './components/expanding-drawer/expanding-drawer.component';
import { UploadCourseDocumentsComponent } from './components/upload-course-documents/upload-course-documents.component';
import { StaffFiltersComponent } from './routes/manage-staff/components/staff-filters/staff-filters.component';
import { CatalogSuggestionsComponent } from './routes/manage-courses/components/catalog-suggestions/catalog-suggestions.component';
import { CatalogSuggestionsDetailsComponent } from './routes/manage-courses/components/catalog-suggestions/components/catalog-suggestions-details/catalog-suggestions-details.component';
import { CatalogSuggestionsResponseComponent } from './routes/manage-courses/components/catalog-suggestions/components/catalog-suggestions-response/catalog-suggestions-response.component';
import { MyCatalogSuggestionsComponent } from './routes/profile/components/my-catalog-suggestions/my-catalog-suggestions.component';
import { MyCatalogSuggestionDetailsComponent } from './routes/profile/components/my-catalog-suggestions/components/my-catalog-suggestion-details/my-catalog-suggestion-details.component';
import { CatalogSuggestionAttachmentsComponent } from './routes/profile/components/my-catalog-suggestions/components/update-my-catalog-suggestion/components/catalog-suggestion-attachments/catalog-suggestion-attachments.component';
import { CatalogSuggestionGeneralInfoComponent } from './routes/profile/components/my-catalog-suggestions/components/update-my-catalog-suggestion/components/catalog-suggestion-general-info/catalog-suggestion-general-info.component';
import { CatalogSuggestionReviewComponent } from './routes/profile/components/my-catalog-suggestions/components/update-my-catalog-suggestion/components/catalog-suggestion-review/catalog-suggestion-review.component';
import { UpdateMyCatalogSuggestionComponent } from './routes/profile/components/my-catalog-suggestions/components/update-my-catalog-suggestion/update-my-catalog-suggestion.component';
import { CatalogSuggestionSubmittedModalComponent } from './routes/profile/components/my-catalog-suggestions/components/catalog-suggestion-submitted-modal/catalog-suggestion-submitted-modal.component';
import { UploadDocumentsModalComponent } from './routes/profile/components/upload-documents-modal/upload-documents-modal.component';
import { UploadCourseDocumentsDialogComponent } from './routes/profile/components/my-course-requests/components/upload-course-documents-dialog/upload-course-documents-dialog.component';

Amplify.configure(environment.amplify);

@NgModule({
  declarations: [
    AppComponent,
    SignInComponent,
    KalpaIconComponent,
    DashboardComponent,
    HeaderComponent,
    FooterComponent,
    LineIconComponent,
    SidebarComponent,
    ManageCoursesIconComponent,
    SystemSettingsComponent,
    SurveysComponent,
    PostsComponent,
    ViewPostComponent,
    EditPostComponent,
    WaitlistsComponent,
    WaitlistComponent,
    PositionsComponent,
    BuildingsComponent,
    TableComponent,
    PositionDetailsDialogComponent,
    DetailsDialogComponent,
    UpdatePositionComponent,
    UpdateBuildingComponent,
    VerificationComponent,
    RequirementsComponent,
    TagsComponent,
    UpdateTagComponent,
    BatchReassignComponent,
    DefaultsComponent,
    GeneralComponent,
    TooltipComponent,
    ApprovalComponent,
    RegistrationNoticeComponent,
    RegistrationMessageComponent,
    EmailNotificationsComponent,
    TagDetailsComponent,
    ChipComponent,
    CourseTypesComponent,
    UpdateCourseTypeComponent,
    CourseTypeDetailsComponent,
    SwapComponent,
    UserRolesComponent,
    UpdateRequirementComponent,
    RequirementDetailsComponent,
    ToastComponent,
    KalpaRedBlackComponent,
    InputComponent,
    SelectComponent,
    ManageStaffComponent,
    StaffListComponent,
    UpdateUserComponent,
    CourseCatalogComponent,
    UpdateCourseComponent,
    ProfileComponent,
    MyPdPlanComponent,
    MyCoursesComponent,
    UpdateStatusModalComponent,
    ValidationComponent,
    CourseValidationDetailsComponent,
    CourseRequestsComponent,
    CourseRequestResponseComponent,
    MyCourseRequestsComponent,
    UpdateMyCourseRequestComponent,
    MyProfileComponent,
    ReassignStaffComponent,
    BuildingReassignmentComponent,
    PrimaryManagerReassignmentComponent,
    SecondaryManagerReassignmentComponent,
    RequirementReassignmentComponent,
    UserRolesReassignmentComponent,
    GeneralInfoComponent,
    CourseDetailsComponent,
    CourseSortingComponent,
    AdditionalDetailsComponent,
    AttachmentsComponent,
    FinishComponent,
    StepperComponent,
    UserGeneralInfoComponent,
    BuildingsAndPositionsComponent,
    UserAssignmentsComponent,
    LicensesAndCertsComponent,
    ReviewAndFinishComponent,
    UserRequirementsComponent,
    CourseFiltersComponent,
    GeneralCourseFiltersComponent,
    CourseBuildingsFiltersComponent,
    CourseRequirementsFiltersComponent,
    CourseCategoriesFiltersComponent,
    CourseTagsFiltersComponent,
    CourseCourseTypesFiltersComponent,
    CatalogCourseDetailsComponent,
    RegistrationRequirementModalComponent,
    RegistrationSuccessModalComponent,
    RegistrationConflictModalComponent,
    RegistrationWaitlistModalComponent,
    UpdateRequirementModalComponent,
    PushModalComponent,
    PushToGroupComponent,
    QuickPeoplePushComponent,
    BulletinWidgetComponent,
    CalendarWidgetComponent,
    StatusWidgetComponent,
    CatalogSuggestionsWidgetComponent,
    CourseRequestsWidgetComponent,
    ValidationWidgetComponent,
    WaitlistWidgetComponent,
    KalpaRedComponent,
    ManageCoursesComponent,
    BreadcrumbsComponent,
    CategoriesComponent,
    UpdateCategoryComponent,
    CategoryDetailsComponent,
    CourseRequestGeneralInfoComponent,
    CourseRequestAttachmentsComponent,
    CourseRequestReviewComponent,
    TreeChecklistComponent,
    ForgotPasswordComponent,
    LogInComponent,
    FirstTimeLoginComponent,
    PasswordChangeComponent,
    SendCodeModalComponent,
    ConfirmLoginComponent,
    ChangePasswordModalComponent,
    MyCourseRequestDetailsComponent,
    RequirementsListComponent,
    CoursesListComponent,
    CourseRequestDetailsComponent,
    CompletePasswordChangeComponent,
    AdvancedFiltersComponent,
    ContentContainerComponent,
    StateDocumentExportComponent,
    ExportSettingsComponent,
    DocumentExportCoursesFilterComponent,
    Error5401Component,
    AddSurveyComponent,
    ButtonGroupComponent,
    NewStepperComponent,
    SearchFilterComponent,
    ModalComponent,
    SelectCoursesComponent,
    SelectRequirementsComponent,
    FinishAndExportComponent,
    SelectCoursesFilterComponent,
    NewInputComponent,
    FormSelectGroupComponent,
    FormSelectOptionComponent,
    FormDatePickerComponent,
    PopUpMenuComponent,
    SsoCallbackComponent,
    UploadFileComponent,
    EntityLogoComponent,
    EditAvatarComponent,
    EditEntityLogoComponent,
    RichTextEditorComponent,
    RichTextEditorValidationDirective,
    RichTextPresenterComponent,
    ExpandingDrawerComponent,
    UploadCourseDocumentsComponent,
    StaffFiltersComponent,
    CatalogSuggestionsComponent,
    CatalogSuggestionsDetailsComponent,
    CatalogSuggestionsResponseComponent,
    MyCatalogSuggestionsComponent,
    MyCatalogSuggestionDetailsComponent,
    CatalogSuggestionAttachmentsComponent,
    CatalogSuggestionGeneralInfoComponent,
    CatalogSuggestionReviewComponent,
    UpdateMyCatalogSuggestionComponent,
    CatalogSuggestionSubmittedModalComponent,
    UploadDocumentsModalComponent,
    UploadCourseDocumentsDialogComponent,
  ],
  imports: [
    BrowserModule,
    EditorModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatMenuModule,
    MatExpansionModule,
    MatDialogModule,
    MatIconModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    DragDropModule,
    FullCalendarModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatTreeModule,
    MatProgressBarModule,
    MatTooltipModule,
    SharedComponentsModule,
  ],
  exports: [],
  providers: [
    AuthService,
    {
      provide: APP_INITIALIZER,
      useFactory: (authService: AuthService) => async () =>
        await authService.autoLogin(),
      deps: [AuthService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheBustingInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
